<!--
  Program: Date Picker
  Author: SDL
  Brief: Date Picker Component
-->
<template>
  <div v-if="lazyLoad" :class="datePickerClass">
    <datepicker
      v-if="!period"
      v-bind="$attrs"
      :ref="refKey"
      :bootstrap-styling="true"
      :wrapper-class="wrapClass"
      :language="currentLang"
      :format="customFormatter"
      :minimum-view="minimumView"
      :disabled-dates="disabledDate"
      :disabled="disabled"
      :calendar-class="calendarClass"
      :typeable="typeable"
      @input="updateDate"
    >
      <div slot="afterDateInput" class="input-group-append">
        <button
          class="btn btn-outline-secondary"
          type="button"
          :data-ref="refKey"
          :disabled="disabled"
          @click="openPicker(refKey)"
          v-click-outside="outsideClick"
        >
        </button>
      </div>
    </datepicker>
    <div class="period" v-else>
      <datepicker
        v-model="startDate"
        :ref="startRefKey"
        :bootstrap-styling="true"
        :wrapper-class="wrapClass"
        :language="currentLang"
        :format="customFormatter"
        :minimum-view="minimumView"
        :disabled-dates="startDisabledDates"
        :disabled="startDisabled || disabled"
        :placeholder="startPlaceHolder"
        :calendar-class="startCalendarClass"
        :data-ref="startRefKey"
        :typeable="typeable"
        @input="updatePeriod"
      >
        <div slot="afterDateInput" class="input-group-append" v-if="!popupDual">
          <button
            class="btn btn-outline-secondary"
            type="button"
            :data-ref="startRefKey"
            :disabled="startDisabled || disabled"
            @click="openPicker(startRefKey)"
            v-click-outside="outsideClick"
          >
          </button>
        </div>
      </datepicker>
      <span class="dash">~</span>
      <datepicker
        v-model="endDate"
        :ref="endRefKey"
        :bootstrap-styling="true"
        :wrapper-class="wrapClass"
        :language="currentLang"
        :format="customFormatter"
        :minimum-view="minimumView"
        :disabled-dates="endDisabledDates"
        :disabled="endDisabled || disabled"
        :placeholder="endPlaceHolder"
        :calendar-class="endCalendarClass"
        :data-ref="endRefKey"
        :typeable="typeable"
        @input="updatePeriod"
      >
        <div slot="afterDateInput" class="input-group-append">
          <button
            class="btn btn-outline-secondary"
            type="button"
            :data-ref="endRefKey"
            :disabled="endDisabled || disabled"
            @click="openPicker(endRefKey)"
            v-click-outside="outsideClick"
          >
          </button>
        </div>
      </datepicker>
    </div>
  </div>
</template>
<script>
import Datepicker from 'vuejs-datepicker';
import { mapGetters } from 'vuex';
import moment from 'moment';
import $ from 'jquery';
import _ from 'lodash';
import SDLUtil from '@/utils/SDLUtil';

export default {
  components: {
    Datepicker,
  },
  props: {
    wrapClass: {
      required: false,
      type: String,
      default: '',
    },
    // period information
    period: {
      required: false,
      type: Object,
    },
    // minimum unit - ex) When setting to month, picker can be selected on a monthly basis
    minimumView: {
      required: false,
      type: String,
      default: 'day',
    },
    default: {
      required: false,
      type: Boolean,
      default: true,
    },
    disabled: {
      required: false,
      type: Boolean,
      default: false,
    },
    disabledDate: {
      required: false,
      type: Object,
      default: () => {},
    },
    startDisabledDate: {
      required: false,
      type: String,
      default: '',
    },
    endDisabledDate: {
      required: false,
      type: String,
      default: '',
    },
    typeable: {
      required: false,
      type: Boolean,
      default: false,
    },
    popupLeft: {
      required: false,
      type: Boolean,
      default: false,
    },
    popupDual: {
      required: false,
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      refKey: _.uniqueId('dp_'),
      startRefKey: _.uniqueId('start_dp_'),
      endRefKey: _.uniqueId('end_dp_'),
      lazyLoad: false,
      startDate: '',
      endDate: '',
      currentLang: null,
      lang: {},
    };
  },
  created() {
    this.init();
  },
  computed: {
    ...mapGetters({
      locale: 'getLocale',
      gmt: 'getGMT',
    }),
    datePickerClass() {
      return [
        'fx_form_date',
        { single: _.isEmpty(this.period) },
      ];
    },
    // Start Date Zone Disabled Settings
    startDisabled() {
      return this.period.startDisabled;
    },
    // End Date Zone Disabled Settings
    endDisabled() {
      return this.period.endDisabled;
    },
    // Start date range settings.
    startDisabledDates() {
      return {
        to: moment(this.startDisabledDate).toDate(),
        from: moment(this.endDate)
          .parseZone()
          .toDate(),
      };
    },
    // End date range settings.
    endDisabledDates() {
      return {
        to: moment(this.startDate).toDate(),
      };
    },
    // Start Date PlaceHolder
    startPlaceHolder() {
      return this.period.startPlaceHolder || '';
    },
    // End Date PlaceHolder
    endPlaceHolder() {
      return this.period.endPlaceHolder || '';
    },
    // Date Format by Locale
    dateFormat() {
      return this.minimumView === 'month'
        ? 'YYYY-MM'
        : 'YYYY-MM-DD';
    },
    // Variable Used for Manipulate DatePicker's "Show Calendar Views Flags" Directly (showDayView, showMonthView, showYearView)
    showView() {
      return `show${_.capitalize(this.minimumView)}View`;
    },
    // Style Class for Calendar
    calendarClass() {
      let classList = ``;
      if (this.popupLeft) classList = `${classList} calendar_left`;
      return classList;
    },
    // Style Class for Start Calendar (when popupDual is true)
    startCalendarClass() {
      let classList = ``;
      if (this.popupDual) classList = `${classList} calendar_start`;
      else if (!this.popupDual && this.popupLeft) classList = `${classList} calendar_left`;
      return classList;
    },
    // Style Class for End Calendar (when popupDual is true)
    endCalendarClass() {
      let classList = ``;
      if (this.popupDual) classList = `${classList} calendar_end`;
      else if (!this.popupDual && this.popupLeft) classList = `${classList} calendar_left`;
      return classList;
    },
  },
  watch: {
    // Change of date unit
    minimumView() {
      this.updatePeriod();
    },
    // locale Change
    locale() {
      this.currentLang = this.lang[this.locale];
    },
    disabledDate(value) {
      this.disabledDate = value;
    },
    gmt() {
      this.init();
    },
  },
  methods: {
    init() {
      this.lazyLoad = false;
      import('vuejs-datepicker/dist/locale/').then(data => {
        this.$i18n.availableLocales.forEach(countryCode => {
          const country = countryCode.replace('_', '-').split('-')[0];
          this.lang[countryCode] = data[country];
        });
        // Applying the language currently set
        this.currentLang = this.lang[this.$i18n.locale];
        // period information initialization
        if (this.period) {
          this.startDate = _.has(this.period, 'endDate')
            ? this.period.startDate
            : moment().subtract(1, 'M').format('YYYY-MM-DD');
          this.endDate = _.has(this.period, 'endDate')
            ? this.period.endDate
            : moment().format('YYYY-MM-DD');
        } else {
          this.startDate = '';
          this.endDate = '';
        }
        if (this.period) {
          this.period.startDate = this.startDate;
          this.period.endDate = this.endDate;
        }
        this.lazyLoad = true;
      });
    },
    /**
     * param: param -> Object
     * return: void
     * exception:
     * description : Close when clicking outside the calendar button area
     */
    outsideClick(param) {
      if (_.find(param.evt.path, ['className', 'vdp-datepicker__calendar'])) {
        return;
      }
      if (
        param.evt.target.className.indexOf('prev') > -1
        || param.evt.target.className.indexOf('next') > -1
        || param.evt.target.className.indexOf('day__month_btn') > -1
        || param.evt.target.className.indexOf('month__year_btn') > -1
        || (param.evt.target.nodeName === 'SPAN' && param.evt.target.parentNode.nodeName === 'HEADER')
      ) {
        return;
      }

      if (this.popupDual) {
        const startCal = this.$refs[this.startRefKey]; // 시작달력
        const endCal = this.$refs[this.endRefKey]; // 종료달력
        // 달력 內 날짜 선택
        if (param.evt.target.className.indexOf(`cell ${this.minimumView}`) > -1) {
          const refKey = param.evt.target.closest('.vdp-datepicker').getAttribute('data-ref');
          if (refKey === this.startRefKey) { // 시작달력 날짜 선택시 시작달력 닫지 않음
            startCal[this.showView] = true;
          } else if (refKey === this.endRefKey) { // 종료달력 날짜 선택시 시작/종료달력 닫음
            startCal.close(true);
            endCal.close(true);
          }
          return;
        }
        // 버튼 외 다른 영역 클릭시 시작/종료달력 닫음
        if ($(param.evt.target).data('ref') !== this.startRefKey && $(param.evt.target).data('ref') !== this.endRefKey) {
          if (startCal.isOpen || endCal.isOpen) {
            startCal.close(true);
            endCal.close(true);
          }
        }
      } else {
        const calendar = this.$refs[$(param.source).data('ref')];
        if (calendar.isOpen) {
          calendar.close(true);
          if (this.$parent.gridClass) this.$parent.gridClass = this.$parent.gridClass.replace(' visible', '');
        }
      }
    },
    /**
     * param: date -> Date
     * return: String
     * exception:
     * description : Return to set date format String
     */
    customFormatter(date = moment()) {
      if (typeof date === 'string') return date;
      if (Object.prototype.toString.call(date).slice(8, -1) === 'Date') {
        const leftPad = value => {
          if (value >= 10) return value;
          return `0${value}`;
        };
        const toStringByFormatting = (source, delimiter = '-') => {
          const year = source.getFullYear();
          const month = leftPad(source.getMonth() + 1);
          const day = leftPad(source.getDate());
          return [year, month, day].join(delimiter);
        };
        return toStringByFormatting(date);
      }
      return moment(date).format(this.dateFormat);
    },
    /**
     * param: refKey -> String
     * return: String
     * exception:
     * description : Activate the calendar by clicking the calendar button
     */
    openPicker(refKey) {
      if (this.popupDual) this.openBothPicker();
      else {
        if (this.$parent.gridClass) this.$parent.gridClass = `${this.$parent.gridClass} visible`;
        this.$refs[refKey].showCalendar();
      }
    },
    /**
     * param: value -> Date
     * return: String
     * exception:
     * description : When changing value updates.
     */
    updateDate(value) {
      this.$emit('input', moment(value).format('YYYY-MM-DD'));
      this.$emit('moment', moment(value));
    },
    /**
     * param: value -> Date
     * return: String
     * exception:
     * description : Start date change
     */
    updateStartDate(value) {
      this.startDate = this.customFormatter(value);
      this.updatePeriod();
    },
    /**
     * param: value -> Date
     * return: String
     * exception:
     * description : End date change
     */
    updateEndDate(value) {
      this.endDate = this.customFormatter(value);
      this.updatePeriod();
    },
    /**
     * param:
     * return: String
     * exception:
     * description : When changing the period updates
     */
    updatePeriod() {
      this.period.startDate = moment(this.startDate).format('YYYY-MM-DD');
      this.period.endDate = moment(this.endDate).format('YYYY-MM-DD');
      this.$emit('input', { ...this.period });
      this.$emit('moment', {
        startDate: moment(this.period.startDate).format('YYYY-MM-DD'),
        endDate: moment(this.period.endDate).format('YYYY-MM-DD'),
      });
    },
    /**
     * param:
     * return:
     * exception:
     * description : open both picker when popupDual is true
     */
    openBothPicker() {
      const startCal = this.$refs[this.startRefKey]; // 시작달력
      const endCal = this.$refs[this.endRefKey]; // 종료달력
      // 시작/종료달력 하나라도 닫혀 있으면 둘다 열기
      if (!startCal.isOpen || !endCal.isOpen) {
        startCal[this.showView] = true;
        endCal[this.showView] = true;
      } else {
        startCal.close();
        endCal.close();
      }
    },
  },
};
</script>

<style>
.wd150 {
  width: 150px !important;
}
.vdp-datepicker__calendar {
  z-index: 118 !important;
  white-space: normal !important;
}
.calendar_left {
  right: 0;
}
.calendar_start {
  right: -10px;
}
.calendar_end {
  left: -8px;
}
</style>



// WEBPACK FOOTER //
// src/components/common/control/datepicker/DatePicker.vue